import {
  ButtonDS,
  DialogModalDS,
  MaterialUiSingleDatePicker,
  NumericInputDS,
  Spacer,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { selectCompany } from "../../../homeSlice";
import { uploadVehicleContractDataAsync } from "../contractFollowUp/vehicleContractDataSlice";
import { AdvancedAnalyticsVehicles } from "../../fleetApi";
import "../../../../../i18n/i18n";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";

type EditContractDataModalProps = {
  visible: boolean;
  onClose: () => void;
  vehicle: Pick<
    AdvancedAnalyticsVehicles,
    | "registrationNumber"
    | "reference"
    | "marque"
    | "modele"
    | "contractMileage"
    | "additionalCostPerKm"
    | "contractStartDate"
    | "contractEndDate"
  >;
};

const areSameDay = (a: Date | null, b: Date | null) =>
  a === null && b === null ? true : a !== null && b !== null && isSameDay(a, b);

export const EditContractDataModal = ({
  visible,
  onClose,
  vehicle,
}: EditContractDataModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);

  const initialContractMileage = vehicle.contractMileage ?? "";
  const initialAdditionalCostPerKm = vehicle.additionalCostPerKm ?? "";
  const initialStartDate = useMemo(
    () =>
      vehicle.contractStartDate ? new Date(vehicle.contractStartDate) : null,
    [vehicle.contractStartDate],
  );
  const initialEndDate = useMemo(
    () => (vehicle.contractEndDate ? new Date(vehicle.contractEndDate) : null),
    [vehicle.contractEndDate],
  );

  const [contractMileage, setContractMileage] = useState<number | "">(
    initialContractMileage,
  );
  const [additionalCostPerKm, setAdditionalCostPerKm] = useState<number | "">(
    initialAdditionalCostPerKm,
  );
  const [contractStartDate, setContractStartDate] = useState<Date | null>(
    initialStartDate,
  );
  const [contractEndDate, setContractEndDate] = useState<Date | null>(
    initialEndDate,
  );
  const [displayError, setDisplayError] = useState<boolean>(false);

  const hasChanges = useMemo(
    () =>
      contractMileage !== initialContractMileage ||
      additionalCostPerKm !== initialAdditionalCostPerKm ||
      !areSameDay(contractStartDate, initialStartDate) ||
      !areSameDay(contractEndDate, initialEndDate),
    [
      contractMileage,
      additionalCostPerKm,
      contractStartDate,
      contractEndDate,
      initialContractMileage,
      initialAdditionalCostPerKm,
      initialStartDate,
      initialEndDate,
    ],
  );

  const allFieldsFilled = useMemo(
    () =>
      contractMileage !== "" &&
      additionalCostPerKm !== "" &&
      contractStartDate !== null &&
      contractEndDate !== null,
    [contractMileage, additionalCostPerKm, contractStartDate, contractEndDate],
  );

  const isEndAfterStart = useMemo(
    () =>
      !contractStartDate ||
      !contractEndDate ||
      contractEndDate > contractStartDate,
    [contractStartDate, contractEndDate],
  );

  const canSubmit = useMemo(
    () => allFieldsFilled && hasChanges && isEndAfterStart,
    [allFieldsFilled, hasChanges, isEndAfterStart],
  );

  const handleSubmit = () => {
    setDisplayError(true);
    if (
      !canSubmit ||
      !company ||
      contractMileage === "" ||
      additionalCostPerKm === "" ||
      !contractStartDate ||
      !contractEndDate
    )
      return;
    void dispatch(
      uploadVehicleContractDataAsync({
        vehicles: [
          {
            registrationNumber: vehicle.registrationNumber,
            reference: vehicle.reference,
            contractMileage,
            additionalCostPerKm: Math.round(Number(additionalCostPerKm) * 100),
            contractStartDate: contractStartDate.toISOString(),
            contractEndDate: contractEndDate.toISOString(),
          },
        ],
        companyUuid: company.uuid,
      }),
    );
    onClose();
  };

  return (
    <DialogModalDS
      visible={visible}
      onClose={onClose}
      title={t("vehicles.sidepanel.editContract.title")}
      width={40}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            sizeButton="S"
            format="fill"
            buttonType="secondary"
            text={t("vehicles.sidepanel.editContract.cancel")}
            onClick={onClose}
          />
        ),
        rightButton: (
          <ButtonDS
            sizeButton="S"
            format="fill"
            buttonType="primary"
            text={t("vehicles.sidepanel.editContract.submit")}
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        ),
      }}
    >
      <StyledContent>
        <StyledVehicleRecap>
          <StyledCarImg src={Car} alt="Car" />
          {vehicle.reference && (
            <>
              <Spacer x={0.75} />
              <Tag
                text={vehicle.reference}
                borderColor={colors["colors/borders/cells/cells"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                textColor={colors["colors/text/darkGrey"]}
              />
            </>
          )}
          <Spacer x={0.75} />
          <StyledRegistration>{vehicle.registrationNumber}</StyledRegistration>
        </StyledVehicleRecap>
        <Spacer y={1.5} />
        <StyledGrid>
          <NumericInputDS
            label={t("vehicles.sidepanel.contractMileage")}
            value={contractMileage}
            update={setContractMileage}
            error={
              displayError && contractMileage === ""
                ? t("vehicles.modal.error.emptyInput")
                : undefined
            }
          />
          <NumericInputDS
            label={t("vehicles.sidepanel.additionalCostPerKm.withUnits")}
            value={additionalCostPerKm}
            update={setAdditionalCostPerKm}
            floatNumber
            maxDigitsAfterDecimal={2}
            error={
              displayError && additionalCostPerKm === ""
                ? t("vehicles.modal.error.emptyInput")
                : undefined
            }
          />
          <MaterialUiSingleDatePicker
            title={t("vehicles.sidepanel.contractStartDate")}
            value={contractStartDate}
            onChange={setContractStartDate}
          />
          <MaterialUiSingleDatePicker
            title={t("vehicles.sidepanel.contractEndDate")}
            value={contractEndDate}
            onChange={setContractEndDate}
          />
        </StyledGrid>
        {displayError && !contractStartDate && (
          <>
            <Spacer y={0.5} />
            <StyledGlobalError>
              <TextCapitalized>
                {t("vehicles.sidepanel.editContract.error.startDateRequired")}
              </TextCapitalized>
            </StyledGlobalError>
          </>
        )}
        {displayError && !contractEndDate && (
          <>
            <Spacer y={0.5} />
            <StyledGlobalError>
              <TextCapitalized>
                {t("vehicles.sidepanel.editContract.error.endDateRequired")}
              </TextCapitalized>
            </StyledGlobalError>
          </>
        )}
        {displayError && !isEndAfterStart && (
          <>
            <Spacer y={0.5} />
            <StyledGlobalError>
              <TextCapitalized>
                {t("vehicles.sidepanel.editContract.error.endBeforeStart")}
              </TextCapitalized>
            </StyledGlobalError>
          </>
        )}
        {displayError && !hasChanges && (
          <>
            <Spacer y={0.5} />
            <StyledGlobalError>
              <TextCapitalized>
                {t("vehicles.sidepanel.editContract.error.noChanges")}
              </TextCapitalized>
            </StyledGlobalError>
          </>
        )}
        <Spacer y={1} />
      </StyledContent>
    </DialogModalDS>
  );
};

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledVehicleRecap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCarImg = styled.img`
  filter: grayscale(100%);
  width: 2.5rem;
  height: 1rem;
  object-fit: contain;
`;

const StyledRegistration = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]};
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
`;

const StyledGlobalError = styled.div`
  ${typographies["Body/XS"]}
  color: ${colors["colors/system/error/error_normal"]};
`;
